<script setup lang="ts">
  import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
  import { useField } from 'vee-validate'
  import { ExclamationCircleIcon, ChevronUpDownIcon, CheckIcon } from '@heroicons/vue/24/outline'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      selectOption: 'Select options...',
      noResults: 'No results found'
    },
    de: {
      selectOption: 'Optionen auswählen...',
      noResults: 'Keine Ergebnisse gefunden'
    }
  })

  export interface Option {
    value: string | number
    title: string
    icon?: string
  }

  const props = defineProps({
    name: {
      type: String,
      required: true
    },
    label: {
      type: String,
      required: false
    },
    modelValue: {
      type: Array as () => (string | number)[],
      required: false,
      default: () => []
    },
    rules: {
      type: [Object, String],
      required: false
    },
    options: {
      type: Array as () => Option[],
      required: true
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    },
    inputAction: {
      type: Function,
      required: false,
      default: (_newVal: string, _oldVal: string) => {},
      validator: (fn) => {
        return typeof fn === 'function' && fn.length === 2
      }
    }
  })

  const emit = defineEmits(['update:modelValue'])

  const {
    value: model,
    errorMessage,
    handleBlur,
    meta
  } = useField(props.name, props.rules, {
    initialValue: props.modelValue
  })

  watch(model, (newValue) => {
    emit('update:modelValue', newValue)
  })

  const selectedOptions = computed(() =>
    props.options.filter((option) => model.value.includes(option.value))
  )

  const opened = ref(false)
  const filterQuery = ref('')
  const inputRef = ref(null)

  const filteredOptions = computed(() => {
    return props.options.filter((option) =>
      option.title.toLowerCase().includes(filterQuery.value.toLowerCase())
    )
  })

  const toggleOption = (option: Option) => {
    const newValue = [...model.value]
    const index = newValue.indexOf(option.value)
    if (index === -1) {
      newValue.push(option.value)
    } else {
      newValue.splice(index, 1)
    }
    model.value = newValue
    filterQuery.value = ''
  }

  const removeOption = (option: Option) => {
    model.value = model.value.filter((value) => value !== option.value)
  }

  const isSelected = (option: Option) => {
    return model.value.includes(option.value)
  }

  const handleClickOutside = (event: MouseEvent) => {
    if (inputRef.value && !inputRef.value.contains(event.target as Node)) {
      opened.value = false
    }
  }

  onMounted(() => {
    document.addEventListener('click', handleClickOutside)
  })

  onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside)
  })

  watch(filterQuery, (newVal, oldVal) => {
    props.inputAction(newVal, oldVal)
  })
</script>

<template>
  <div>
    <label
      v-if="label"
      class="mb-2 block text-sm font-medium"
    >
      {{ label }}
    </label>
    <div
      ref="inputRef"
      class="relative"
    >
      <div
        class="relative flex min-h-[46px] w-full cursor-pointer flex-wrap items-center rounded-lg border bg-white
          px-4 py-2 text-start text-sm focus:border-purple-500 focus:ring-purple-500 disabled:opacity-50"
        :class="[
          errorMessage
            ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
            : 'border-gray-200'
        ]"
        @click="opened = true"
        @blur="handleBlur"
      >
        <div
          v-for="option in selectedOptions"
          :key="option.value"
          class="m-1 flex items-center rounded-full border border-gray-200 bg-white p-1"
        >
          <div
            v-if="option.icon"
            class="me-1 size-6"
            v-html="option.icon"
          ></div>
          <div class="whitespace-nowrap ps-2 text-gray-800">{{ option.title }}</div>
          <button
            class="ms-2 inline-flex size-5 items-center justify-center rounded-full bg-gray-200 text-gray-800
              hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-400"
            @click.stop="removeOption(option)"
          >
            <svg
              class="size-3"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        <input
          v-model="filterQuery"
          class="flex-grow border-none bg-transparent p-0 focus:outline-none focus:ring-0"
          type="text"
          :placeholder="selectedOptions.length === 0 ? t('selectOption') : ''"
          :disabled="disabled"
          @click.stop="opened = true"
        />
      </div>
      <div
        v-if="errorMessage"
        class="absolute end-8 top-1/2 -translate-y-1/2"
      >
        <ExclamationCircleIcon class="size-5 flex-shrink-0 text-red-500" />
      </div>
      <ChevronUpDownIcon class="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" />
      <div
        v-show="opened"
        class="absolute z-20 mt-2 max-h-72 w-full space-y-0.5 overflow-hidden overflow-y-auto rounded-lg border
          border-gray-200 bg-white px-1 py-1"
      >
        <div
          v-if="filteredOptions.length === 0"
          class="px-4 py-2 text-sm text-gray-500"
        >
          {{ t('noResults') }}
        </div>
        <div
          v-for="option in filteredOptions"
          v-else
          :key="option.value"
          class="w-full cursor-pointer rounded-lg px-4 py-2 text-sm text-gray-800 hover:bg-gray-100 focus:bg-gray-100
            focus:outline-none disabled:opacity-50"
          @click="toggleOption(option)"
        >
          <div class="flex items-center">
            <div
              v-if="option.icon"
              class="me-2 size-6"
              v-html="option.icon"
            ></div>
            <div class="text-gray-800">
              {{ option.title }}
            </div>
            <CheckIcon
              v-if="isSelected(option)"
              class="ms-auto size-5 text-purple-500"
            />
          </div>
        </div>
      </div>
      <p
        v-if="errorMessage"
        class="mt-1 text-xs text-red-600"
      >
        {{ errorMessage }}
      </p>
    </div>
  </div>
</template>
